import {
  View,
  Text,
  StyleSheet,
  type ViewStyle,
  Platform,
} from "react-native";
import { OTPInput, type SlotProps , OTPInputRef } from "input-otp-native";
import { OTPInput as OTPInputWeb } from "input-otp";
import { useRef , useEffect } from "react";

import Animated, {
  useAnimatedStyle,
  withRepeat,
  withTiming,
  withSequence,
  useSharedValue,
} from "react-native-reanimated";

export function GroupDeviceOTPInput(props: {
  onComplete: (code: string) => void;
  disabled?: boolean;
}) {
  const ref = useRef<OTPInputRef>(null);
  const onComplete = (code: string) => {
    props.onComplete(code);
    ref.current?.clear();
  };
  if (Platform.OS === "web") {
    return (
      <View style={styles.container}>
        <OTPInputWeb
          disabled={props.disabled}
          autoFocus
          onComplete={onComplete}
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          maxLength={8}
          render={({ slots }) => (
            <View style={styles.slotsContainer}>
              {slots.map((slot, idx) => (
                <Slot key={idx} {...slot} />
              ))}
            </View>
          )}
        />
      </View>
    );
  }

  return (
    <View style={{ flex: 1, alignItems: "center" }}>
      <OTPInput
        ref={ref}
        onComplete={onComplete}
        inputMode="text"
        autoCapitalize="none"
        autoCorrect={false}
        containerStyle={styles.container}
        maxLength={8}
        render={({ slots }) => (
          <View style={styles.slotsContainer}>
            {slots.map((slot, idx) => (
              <Slot key={idx} {...slot} />
            ))}
          </View>
        )}
      />
    </View>
  );
}

function Slot({ char, isActive, hasFakeCaret }: SlotProps) {
  return (
    <View style={[styles.slot, isActive && styles.activeSlot]}>
      {char !== null && <Text style={styles.char}>{char}</Text>}
      {hasFakeCaret && <FakeCaret />}
    </View>
  );
}

function FakeCaret({ style }: { style?: ViewStyle }) {
  const opacity = useSharedValue(1);

  useEffect(() => {
    opacity.value = withRepeat(
      withSequence(
        withTiming(0, { duration: 500 }),
        withTiming(1, { duration: 500 })
      ),
      -1,
      true
    );
  }, [opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  return (
    <View style={styles.fakeCaretContainer}>
      <Animated.View style={[styles.fakeCaret, style, animatedStyle]} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  slotsContainer: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  slot: {
    width: 40,
    height: 40,
    aspectRatio: 1,
    maxWidth: "11%",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  activeSlot: {
    borderColor: "#000000",
    borderWidth: 2,
  },
  char: {
    fontSize: 20,
    fontWeight: "500",
    color: "#111827",
  },
  fakeCaretContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  fakeCaret: {
    width: 2,
    height: 20,
    backgroundColor: "#000",
    borderRadius: 1,
  },
});
